// El monto escrito en letras, para el comprobante de egreso.
//
// ── Por qué esto no se escribe a mano ──
//
// En el comprobante van las dos cosas: la cifra y «la suma en letras». Están
// las dos justamente porque la letra es la que manda si no coinciden — es lo
// que impide que alguien le agregue un cero al número después de firmado.
//
// Escribiéndolas a mano las dos, el error de tecleo aparece EN EL PAPEL YA
// FIRMADO, y ahí no se puede arreglar: hay que anular el comprobante. Salidas
// de la misma cifra, no pueden discrepar.
//
// El español tiene sus trampas y todas están probadas: «cien» pero «ciento
// uno», «mil» pero no «un mil», «veintiún mil» pero «veintiuno» solo.

const UNIDADES = ['', 'uno', 'dos', 'tres', 'cuatro', 'cinco', 'seis', 'siete',
  'ocho', 'nueve', 'diez', 'once', 'doce', 'trece', 'catorce', 'quince',
  'dieciséis', 'diecisiete', 'dieciocho', 'diecinueve', 'veinte', 'veintiuno',
  'veintidós', 'veintitrés', 'veinticuatro', 'veinticinco', 'veintiséis',
  'veintisiete', 'veintiocho', 'veintinueve'];

const DECENAS = ['', '', '', 'treinta', 'cuarenta', 'cincuenta', 'sesenta',
  'setenta', 'ochenta', 'noventa'];

const CENTENAS = ['', 'ciento', 'doscientos', 'trescientos', 'cuatrocientos',
  'quinientos', 'seiscientos', 'setecientos', 'ochocientos', 'novecientos'];

// 0 a 99. De 21 a 29 van pegadas —«veintidós»— y de 31 en adelante con «y».
function hastaNoventaYNueve(n) {
  if (n < 30) return UNIDADES[n];
  const d = Math.floor(n / 10), u = n % 10;
  return DECENAS[d] + (u ? ' y ' + UNIDADES[u] : '');
}

// 0 a 999. «Cien» solo cuando es exacto: 101 es «ciento uno», no «cien uno».
function hastaNovecientos(n) {
  if (n === 100) return 'cien';
  const c = Math.floor(n / 100), r = n % 100;
  const cabeza = CENTENAS[c];
  const cola = hastaNoventaYNueve(r);
  return [cabeza, cola].filter(Boolean).join(' ');
}

// Delante de un sustantivo masculino, «uno» se apocopa: veintiún mil, treinta
// y un millones. Sin esto sale «veintiuno mil», que en un papel firmado se lee
// como que lo llenó una máquina que no sabe español.
// Ojo con dónde termina: no es solo «uno» suelto. «Ciento uno pesos» y
// «treinta y uno mil» están igual de mal, y la primera versión solo cazaba los
// dos casos que se me ocurrieron de memoria.
function apocopar(texto) {
  if (texto.endsWith('veintiuno')) return texto.slice(0, -9) + 'veintiún';
  if (texto === 'uno') return 'un';
  if (texto.endsWith(' uno')) return texto.slice(0, -3) + 'un';
  return texto;
}

// El número entero en letras. Hasta 999.999.999.999.
export function numeroALetras(n) {
  const entero = Math.floor(Math.abs(Number(n) || 0));
  if (entero === 0) return 'cero';

  const partes = [];
  const millones = Math.floor(entero / 1000000);
  const miles = Math.floor((entero % 1000000) / 1000);
  const resto = entero % 1000;

  if (millones > 0) {
    // «Un millón», no «uno millón». Y de dos en adelante, «millones».
    partes.push(millones === 1
      ? 'un millón'
      : apocopar(numeroALetras(millones)) + ' millones');
  }
  if (miles > 0) {
    // «Mil», no «un mil»: en español el uno delante de mil no se dice.
    partes.push(miles === 1 ? 'mil' : apocopar(hastaNovecientos(miles)) + ' mil');
  }
  if (resto > 0) partes.push(hastaNovecientos(resto));

  return partes.join(' ');
}

// Como va en el comprobante: en mayúscula sostenida y con la moneda.
//
// «M/CTE» —moneda corriente— es lo que se usa en un comprobante colombiano y
// lo que el contador espera ver.
export function enPesos(n, { centavos = false } = {}) {
  const monto = Math.abs(Number(n) || 0);
  const entero = Math.floor(monto);
  const cent = Math.round((monto - entero) * 100);

  // «Peso» es masculino, así que aquí también se apocopa: UN PESO, VEINTIÚN
  // PESOS, CIENTO UN PESOS.
  let texto = apocopar(numeroALetras(entero)).toUpperCase();
  texto += entero === 1 ? ' PESO' : ' PESOS';
  // Los centavos solo si los hay y si se piden. Un «CON CERO CENTAVOS» en cada
  // comprobante es ruido que se deja de leer.
  if (centavos && cent > 0) {
    texto += ' CON ' + numeroALetras(cent).toUpperCase() +
             (cent === 1 ? ' CENTAVO' : ' CENTAVOS');
  }
  return texto + ' M/CTE';
}
